import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  obtenerSabores,
  obtenerBodegas,
  obtenerInventarioHelados,
  guardarInventarioHelados,
  registrarDistribucion,
} from '../../utils/api';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

// ─────────────────────────────────────────────
// COLORES POR SABOR
// ─────────────────────────────────────────────
const COLORES_SABOR = {
  'Vainilla':    { bg: '#FFF8E1', fill: '#FFD54F', text: '#5D4037' },
  'Chocolate':   { bg: '#EFEBE9', fill: '#6D4C41', text: '#3E2723' },
  'Fresa':       { bg: '#FCE4EC', fill: '#F06292', text: '#880E4F' },
  'Arequipe':    { bg: '#FFF3E0', fill: '#FFA726', text: '#E65100' },
  'Maracuyá':    { bg: '#FFFDE7', fill: '#FFD600', text: '#F57F17' },
  'Limón':       { bg: '#F9FBE7', fill: '#C6E84E', text: '#558B2F' },
  'Mora':        { bg: '#F3E5F5', fill: '#9C27B0', text: '#4A148C' },
  'Coco':        { bg: '#F5F5F5', fill: '#E0E0E0', text: '#424242' },
  'Mango':       { bg: '#FFF8E1', fill: '#FF8F00', text: '#E65100' },
  'Pistacho':    { bg: '#E8F5E9', fill: '#66BB6A', text: '#1B5E20' },
};
const COLOR_DEFAULT = { bg: '#EEF2FF', fill: '#818CF8', text: '#312E81' };

// ─────────────────────────────────────────────
// COMPONENTE VISUAL: BANDEJA
// ─────────────────────────────────────────────
function BandejaVisual({ nivel, onChange, color, disabled }) {
  // nivel: 0=vacío, 1=1/3, 2=2/3, 3=llena
  const segmentos = [1, 2, 3];

  return (
    <div style={bv.wrapper} title={['Vacía', '1/3', '2/3', 'Llena'][nivel]}>
      <div style={bv.bandeja}>
        {/* Borde tipo bandeja */}
        <div style={bv.rim} />
        {/* Segmentos */}
        <div style={bv.segments}>
          {segmentos.map((seg) => (
            <button
              key={seg}
              disabled={disabled}
              onClick={() => !disabled && onChange(nivel === seg ? seg - 1 : seg)}
              style={{
                ...bv.seg,
                background: seg <= nivel ? color.fill : 'rgba(255,255,255,0.4)',
                cursor: disabled ? 'default' : 'pointer',
                borderRight: seg < 3 ? '1px dashed rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {seg <= nivel && (
                <div style={bv.scoopRow}>
                  <div style={{ ...bv.scoop, background: color.fill, filter: 'brightness(0.85)' }} />
                  <div style={{ ...bv.scoop, background: color.fill, filter: 'brightness(0.9)', marginTop: '-4px' }} />
                </div>
              )}
            </button>
          ))}
        </div>
        {/* Nivel label */}
        <div style={{ ...bv.label, color: color.text }}>
          {['Vacía', '⅓', '⅔', 'Llena'][nivel]}
        </div>
      </div>
    </div>
  );
}

const bv = {
  wrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  bandeja: {
    position: 'relative', width: 90, background: 'rgba(255,255,255,0.5)',
    borderRadius: '6px 6px 14px 14px',
    border: '2px solid rgba(0,0,0,0.12)',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 3px rgba(255,255,255,0.8)',
  },
  rim: {
    height: 6, background: 'rgba(0,0,0,0.08)',
    borderBottom: '1px solid rgba(0,0,0,0.1)',
  },
  segments: { display: 'flex', height: 54 },
  seg: {
    flex: 1, border: 'none', padding: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s', position: 'relative',
  },
  scoopRow: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  scoop: {
    width: 18, height: 16, borderRadius: '50% 50% 40% 40%',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  label: {
    textAlign: 'center', fontSize: 10, fontWeight: 700,
    padding: '3px 0 5px', background: 'rgba(255,255,255,0.6)',
    letterSpacing: '0.05em',
  },
};

// ─────────────────────────────────────────────
// COMPONENTE: CONTADOR DE BANDEJAS
// ─────────────────────────────────────────────
function ContadorBandejas({ valor, onChange, label, icon, disabled }) {
  return (
    <div style={cb.wrapper}>
      <div style={cb.label}>{icon} {label}</div>
      <div style={cb.control}>
        <button
          disabled={disabled || valor <= 0}
          onClick={() => onChange(Math.max(0, valor - 1))}
          style={{ ...cb.btn, opacity: (disabled || valor <= 0) ? 0.4 : 1 }}
        >−</button>
        <div style={cb.valor}>{valor}</div>
        <button
          disabled={disabled}
          onClick={() => onChange(valor + 1)}
          style={cb.btn}
        >+</button>
      </div>
    </div>
  );
}

const cb = {
  wrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  label: { fontSize: 11, fontWeight: 600, color: '#6B7280', textAlign: 'center' },
  control: { display: 'flex', alignItems: 'center', gap: 4 },
  btn: {
    width: 28, height: 28, border: '2px solid #E5E7EB',
    borderRadius: 8, background: 'white', cursor: 'pointer',
    fontSize: 16, fontWeight: 700, color: '#374151',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
  },
  valor: {
    width: 36, height: 28, background: '#F9FAFB',
    border: '2px solid #E5E7EB', borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 700, color: '#111827',
  },
};

// ─────────────────────────────────────────────
// TARJETA DE SABOR
// ─────────────────────────────────────────────
function TarjetaSabor({ sabor, registro, onChange, modoProduccion, disabled }) {
  const color = COLORES_SABOR[sabor.nombre] || COLOR_DEFAULT;

  return (
    <div style={{
      ...ts.card,
      background: color.bg,
      border: sabor.es_premium
        ? '2px solid #F59E0B'
        : `2px solid ${color.fill}30`,
    }}>
      {sabor.es_premium && (
        <div style={ts.premiumBadge}>⭐ Premium</div>
      )}

      <div style={ts.nombre} title={sabor.nombre}>
        <span style={{ ...ts.nombreDot, background: color.fill }} />
        {sabor.nombre}
      </div>

      {modoProduccion ? (
        // Producción: solo bandejas completas
        <div style={ts.produccionArea}>
          <ContadorBandejas
            valor={registro.bandejas_produccion}
            onChange={(v) => onChange({ ...registro, bandejas_produccion: v })}
            label="Bandejas en bodega"
            icon="🧊"
            disabled={disabled}
          />
        </div>
      ) : (
        // Punto de venta: bandeja visual + congelador
        <div style={ts.pvArea}>
          <div style={ts.bandejaSection}>
            <div style={ts.bandejaLabel}>En vitrina</div>
            <BandejaVisual
              nivel={registro.nivel_bandeja}
              onChange={(v) => onChange({ ...registro, nivel_bandeja: v })}
              color={color}
              disabled={disabled}
            />
          </div>
          <div style={ts.divider} />
          <ContadorBandejas
            valor={registro.bandejas_congelador}
            onChange={(v) => onChange({ ...registro, bandejas_congelador: v })}
            label="En congelador"
            icon="🧊"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

const ts = {
  card: {
    borderRadius: 14, padding: '14px 12px', position: 'relative',
    display: 'flex', flexDirection: 'column', gap: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  premiumBadge: {
    position: 'absolute', top: 8, right: 8,
    background: '#FEF3C7', color: '#92400E',
    fontSize: 10, fontWeight: 700, padding: '2px 7px',
    borderRadius: 20, border: '1px solid #F59E0B',
  },
  nombre: {
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 13, fontWeight: 700, color: '#1F2937',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  nombreDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  pvArea: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-around', gap: 8,
  },
  bandejaSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  bandejaLabel: { fontSize: 11, fontWeight: 600, color: '#6B7280' },
  divider: { width: 1, height: 60, background: 'rgba(0,0,0,0.1)' },
  produccionArea: { display: 'flex', justifyContent: 'center', paddingTop: 8 },
};

// ─────────────────────────────────────────────
// MODAL: REGISTRAR DISTRIBUCIÓN
// ─────────────────────────────────────────────
function ModalDistribucion({ sabores, bodegas, onClose, onSave }) {
  const [saborId, setSaborId] = useState('');
  const [bodegaId, setBodegaId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  const puntos = bodegas.filter(b => b.tipo === 'punto_venta');

  const handleSave = async () => {
    if (!saborId || !bodegaId || cantidad < 1) {
      toast.error('Completa todos los campos');
      return;
    }
    setGuardando(true);
    try {
      await registrarDistribucion({
        sabor_id: saborId,
        bodega_destino_id: bodegaId,
        cantidad_bandejas: cantidad,
        notas,
      });
      toast.success('Distribución registrada');
      onSave();
      onClose();
    } catch {
      toast.error('Error al registrar distribución');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={md.overlay}>
      <div style={md.modal}>
        <div style={md.header}>
          <h3 style={md.title}>🚚 Registrar Distribución</h3>
          <button onClick={onClose} style={md.closeBtn}>✕</button>
        </div>

        <div style={md.body}>
          <div style={md.field}>
            <label style={md.label}>Sabor</label>
            <select value={saborId} onChange={e => setSaborId(e.target.value)} style={md.select}>
              <option value="">Seleccionar sabor...</option>
              {sabores.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}{s.es_premium ? ' ⭐' : ''}</option>
              ))}
            </select>
          </div>

          <div style={md.field}>
            <label style={md.label}>Destino</label>
            <select value={bodegaId} onChange={e => setBodegaId(e.target.value)} style={md.select}>
              <option value="">Seleccionar punto de venta...</option>
              {puntos.map(b => (
                <option key={b.id} value={b.id}>{b.nombre}</option>
              ))}
            </select>
          </div>

          <div style={md.field}>
            <label style={md.label}>Cantidad de bandejas (llenas)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => setCantidad(Math.max(1, cantidad - 1))} style={md.countBtn}>−</button>
              <span style={md.countVal}>{cantidad}</span>
              <button onClick={() => setCantidad(cantidad + 1)} style={md.countBtn}>+</button>
            </div>
          </div>

          <div style={md.field}>
            <label style={md.label}>Notas (opcional)</label>
            <textarea
              value={notas}
              onChange={e => setNotas(e.target.value)}
              style={md.textarea}
              placeholder="Alguna observación..."
              rows={2}
            />
          </div>
        </div>

        <div style={md.footer}>
          <button onClick={onClose} style={md.btnSecundario}>Cancelar</button>
          <button onClick={handleSave} disabled={guardando} style={md.btnPrimario}>
            {guardando ? 'Guardando...' : '✓ Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const md = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: 16, width: '100%', maxWidth: 420,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px 16px', borderBottom: '1px solid #F3F4F6',
  },
  title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 18,
    cursor: 'pointer', color: '#6B7280', padding: '4px 8px',
  },
  body: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: '#374151' },
  select: {
    padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 8,
    fontSize: 14, outline: 'none', background: 'white',
  },
  countBtn: {
    width: 36, height: 36, border: '2px solid #E5E7EB', borderRadius: 8,
    background: 'white', cursor: 'pointer', fontSize: 18, fontWeight: 700,
  },
  countVal: {
    width: 48, height: 36, background: '#F9FAFB', border: '2px solid #E5E7EB',
    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 700, color: '#111827',
  },
  textarea: {
    padding: '10px 12px', border: '2px solid #E5E7EB', borderRadius: 8,
    fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit',
  },
  footer: {
    display: 'flex', gap: 10, padding: '16px 24px',
    borderTop: '1px solid #F3F4F6', justifyContent: 'flex-end',
  },
  btnSecundario: {
    padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8,
    background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151',
  },
  btnPrimario: {
    padding: '10px 24px', border: 'none', borderRadius: 8,
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
};

// ─────────────────────────────────────────────
// PÁGINA PRINCIPAL: INVENTARIO HELADOS
// ─────────────────────────────────────────────
export default function InventarioHelados() {
  const { usuario, puedeVer } = useAuth();
  const [sabores, setSabores] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [bodegaSeleccionada, setBodegaSeleccionada] = useState(null);
  const [registros, setRegistros] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [modalDist, setModalDist] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [bloqueado, setBloqueado] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalCorreccion, setModalCorreccion] = useState(false);

  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [fechaVer, setFechaVer] = useState(hoy);
  const [vistaTab, setVistaTab] = useState('inventario'); // 'inventario' | 'historial' | 'kardex'
  const [historial, setHistorial] = useState([]);
  const [registroDetalle, setRegistroDetalle] = useState(null);
  const [kardex, setKardex]       = useState(null);
  const [cargandoKardex, setCargandoKardex] = useState(false);
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [desdeKardex, setDesdeKardex] = useState(inicioMes);
  const [hastaKardex, setHastaKardex] = useState(hoy); // {fecha, sabores:[]}
  const modoProduccion = bodegaSeleccionada?.tipo === 'produccion';
  const soloLectura = !puedeVer('produccion', 'administracion', 'gerencia', 'punto_venta');

  // Cargar catálogos
  useEffect(() => {
    Promise.all([obtenerSabores(), obtenerBodegas()])
      .then(([sResp, bResp]) => {
        setSabores(sResp.data);
        const bods = bResp.data;
        setBodegas(bods);

        // Seleccionar bodega según rol
        let bodegaInicial;
        if (puedeVer('punto_venta')) {
          bodegaInicial = bods.find(b => b.area_id === usuario.area_id);
        } else {
          // Gerencia, administracion y produccion ven todas las bodegas
          bodegaInicial = bods[0];
        }
        if (bodegaInicial) setBodegaSeleccionada(bodegaInicial);
      })
      .catch(() => toast.error('Error cargando datos'))
      .finally(() => setCargando(false));
  }, []);

  // Cargar inventario cuando cambia la bodega
  useEffect(() => {
    if (!bodegaSeleccionada || sabores.length === 0) return;

    setCargando(true);
    obtenerInventarioHelados(bodegaSeleccionada.id, fechaVer)
      .then(({ data }) => {
        const mapa = {};
        data.inventario.forEach(item => {
          mapa[item.sabor_id] = {
            sabor_id: item.sabor_id,
            nivel_bandeja: item.nivel_bandeja,
            bandejas_congelador: item.bandejas_congelador,
            bandejas_produccion: item.bandejas_produccion,
          };
        });
        setRegistros(mapa);
        setBloqueado(data.bloqueado || false);
        setGuardadoOk(false);
      })
      .catch(() => toast.error('Error cargando inventario'))
      .finally(() => setCargando(false));
  }, [bodegaSeleccionada, sabores, fechaVer]);

  // Cargar historial
  useEffect(() => {
    if (!bodegaSeleccionada || vistaTab !== 'historial') return;
    API.get(`/helados/historial/${bodegaSeleccionada.id}`)
      .then(r => setHistorial(r.data || [])).catch(() => {});
  }, [bodegaSeleccionada, vistaTab]);

  const cargarKardex = () => {
    if (!bodegaSeleccionada) return;
    setCargandoKardex(true);
    setKardex(null);
    API.get('/helados/kardex', { params: { bodega_id: bodegaSeleccionada.id, desde: desdeKardex, hasta: hastaKardex } })
      .then(r => setKardex(r.data))
      .catch(() => toast.error('Error cargando kardex'))
      .finally(() => setCargandoKardex(false));
  };

  const verRegistro = async (fecha) => {
    try {
      const { data } = await obtenerInventarioHelados(bodegaSeleccionada.id, fecha);
      const conDatos = data.inventario.filter(s => s.nivel_bandeja > 0 || s.bandejas_congelador > 0 || s.bandejas_produccion > 0);
      setRegistroDetalle({ fecha, sabores: conDatos, bloqueado: data.bloqueado });
    } catch { toast.error('Error cargando registro'); }
  };

  const handleChange = (saborId, nuevoRegistro) => {
    if (bloqueado) return;
    setRegistros(prev => ({ ...prev, [saborId]: nuevoRegistro }));
    setGuardadoOk(false);
  };

  const getRegistro = (sabor) => registros[sabor.id] || {
    sabor_id: sabor.id,
    nivel_bandeja: 0,
    bandejas_congelador: 0,
    bandejas_produccion: 0,
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await guardarInventarioHelados({
        bodega_id: bodegaSeleccionada.id,
        registros: sabores.map(s => ({
          sabor_id: s.id,
          ...getRegistro(s),
        })),
      });
      setGuardadoOk(true);
      toast.success('¡Inventario guardado!');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleConfirmarYBloquear = async () => {
    setGuardando(true);
    try {
      await guardarInventarioHelados({
        bodega_id: bodegaSeleccionada.id,
        registros: sabores.map(s => ({ sabor_id: s.id, ...getRegistro(s) })),
      });
      await API.post(`/helados/inventario/${bodegaSeleccionada.id}/${fechaVer}/bloquear`);
      setBloqueado(true);
      setGuardadoOk(true);
      toast.success('¡Inventario enviado y bloqueado!');
    } catch {
      toast.error('Error al bloquear inventario');
    } finally {
      setGuardando(false);
      setModalConfirm(false);
    }
  };

  // Estadísticas rápidas
  const totalBandejasVitrina = sabores.reduce((acc, s) => acc + (getRegistro(s).nivel_bandeja > 0 ? 1 : 0), 0);
  const saboresAgotados = sabores.filter(s => {
    const r = getRegistro(s);
    return r.nivel_bandeja === 0 && r.bandejas_congelador === 0 && r.bandejas_produccion === 0;
  });
  const saboresPremium = sabores.filter(s => s.es_premium);

  if (cargando && sabores.length === 0) {
    return (
      <div style={p.loadingWrap}>
        <div style={p.loadingIcon}>🍦</div>
        <p>Cargando inventario...</p>
      </div>
    );
  }

  return (
    <div style={p.root}>
      {/* Header */}
      <div style={p.header}>
        <div>
          <h1 style={p.titulo}>
            <span style={p.tituloIcon}>🍦</span>
            Inventario de Helados
          </h1>
          <p style={p.subtitulo}>
            {format(new Date(), "EEEE d 'de' MMMM, yyyy", { locale: es })}
          </p>
        </div>

        <div style={p.headerActions}>
          {puedeVer('produccion', 'administracion', 'gerencia') && (
            <button onClick={() => setModalDist(true)} style={p.btnDistribucion}>
              🚚 Registrar Distribución
            </button>
          )}
          {!bloqueado ? (
            <>
              <button
                onClick={handleGuardar}
                disabled={guardando}
                style={{ ...p.btnGuardar, ...(guardadoOk ? p.btnGuardarOk : {}) }}
              >
                {guardando ? '⏳ Guardando...' : guardadoOk ? '✓ Borrador' : '💾 Guardar Borrador'}
              </button>
              <button
                onClick={() => setModalConfirm(true)}
                disabled={guardando}
                style={{ ...p.btnGuardar, background: 'linear-gradient(135deg,#1D4ED8,#1E40AF)', marginLeft: 8 }}
              >
                📤 Confirmar y Bloquear
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 700 }}>
                🔒 Inventario bloqueado
              </span>
              {!puedeVer('administracion', 'gerencia') && (
                <button onClick={() => setModalCorreccion(true)} style={{ padding: '8px 14px', border: '1px solid #F59E0B', borderRadius: 8, background: 'white', color: '#92400E', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  ✏️ Solicitar Corrección
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selector de bodega */}
      {puedeVer('gerencia', 'administracion', 'produccion') && (
        <div style={p.bodegaRow}>
          {bodegas.map(b => (
            <button
              key={b.id}
              onClick={() => setBodegaSeleccionada(b)}
              style={{
                ...p.bodegaBtn,
                ...(bodegaSeleccionada?.id === b.id ? p.bodegaBtnActive : {}),
              }}
            >
              {b.tipo === 'produccion' ? '🏭' : '🏪'} {b.nombre}
            </button>
          ))}
        </div>
      )}

      {/* Tabs + Fecha */}
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ display:'flex', background:'#F3F4F6', borderRadius:8, padding:2 }}>
          {[{id:'inventario',label:'📝 Inventario'},{id:'historial',label:'📋 Historial'},{id:'kardex',label:'📊 Kardex'}].map(t => (
            <button key={t.id} onClick={() => setVistaTab(t.id)} style={{
              padding:'6px 16px', border:'none', borderRadius:7, cursor:'pointer', fontSize:13,
              fontWeight: vistaTab===t.id?700:500,
              background: vistaTab===t.id?'white':'transparent',
              color: vistaTab===t.id?'#111827':'#6B7280',
              boxShadow: vistaTab===t.id?'0 1px 3px rgba(0,0,0,0.1)':'none'
            }}>{t.label}</button>
          ))}
        </div>
        {vistaTab === 'inventario' && (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>Fecha</label>
            <input type="date" value={fechaVer} onChange={e => setFechaVer(e.target.value)} max={hoy}
              style={{ padding:'6px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
            {fechaVer !== hoy && (
              <span style={{ fontSize:11, background:'#FEF3C7', color:'#92400E', padding:'3px 8px', borderRadius:6, fontWeight:600 }}>
                📅 Viendo fecha anterior
              </span>
            )}
          </div>
        )}
      </div>

      {/* Historial */}
      {vistaTab === 'historial' && !registroDetalle && (
        <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:20 }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>📋 Historial de registros — {bodegaSeleccionada?.nombre}</div>
          {historial.length === 0 ? (
            <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin registros históricos</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Fecha','Sabores registrados','Estado',''].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historial.map((h,i) => (
                  <tr key={i} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding:'10px 16px', fontWeight:700 }}>
                      {h.fecha ? format(new Date(h.fecha.slice(0,10)+'T12:00'), "d 'de' MMMM yyyy", {locale:es}) : '—'}
                    </td>
                    <td style={{ padding:'10px 16px', color:'#6B7280' }}>{h.total_sabores || 0} sabores</td>
                    <td style={{ padding:'10px 16px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                        background: h.bloqueado?'#D1FAE5':'#FEF3C7',
                        color: h.bloqueado?'#065F46':'#92400E' }}>
                        {h.bloqueado ? '🔒 Bloqueado' : '📝 Borrador'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <button onClick={() => verRegistro(h.fecha.slice(0,10))}
                        style={{ padding:'4px 12px', border:'1px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                        Ver registro
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Detalle de registro histórico */}
      {vistaTab === 'historial' && registroDetalle && (
        <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:20 }}>
          <div style={{ background:'#1a1a2e', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ color:'white', fontWeight:800, fontSize:15 }}>
                🍦 Registro del {format(new Date(registroDetalle.fecha+'T12:00'), "d 'de' MMMM yyyy", {locale:es})}
              </div>
              <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginTop:2 }}>
                {bodegaSeleccionada?.nombre} · {registroDetalle.sabores.length} sabores con stock
                {registroDetalle.bloqueado && <span style={{ marginLeft:8, background:'#D1FAE5', color:'#065F46', padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:700 }}>🔒 Bloqueado</span>}
              </div>
            </div>
            <button onClick={() => setRegistroDetalle(null)}
              style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:13, fontWeight:700 }}>
              ← Volver
            </button>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Sabor','Nivel vitrina','Bandejas congelador','Bandejas producción'].map(h => (
                    <th key={h} style={{ padding:'10px 16px', textAlign: h==='Sabor'?'left':'center', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registroDetalle.sabores.map((s,i) => (
                  <tr key={s.sabor_id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                    <td style={{ padding:'10px 16px', fontWeight:600 }}>{s.sabor || s.nombre}</td>
                    <td style={{ padding:'10px 16px', textAlign:'center' }}>
                      <div style={{ display:'inline-flex', gap:3 }}>
                        {[1,2,3].map(n => (
                          <div key={n} style={{ width:14, height:14, borderRadius:3, background: n <= s.nivel_bandeja ? '#3B4E8C' : '#E5E7EB' }} />
                        ))}
                      </div>
                    </td>
                    <td style={{ padding:'10px 16px', textAlign:'center', fontWeight:700, color:'#111827' }}>{s.bandejas_congelador || 0}</td>
                    <td style={{ padding:'10px 16px', textAlign:'center', fontWeight:700, color:'#6366F1' }}>{s.bandejas_produccion || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {vistaTab === 'inventario' && (
      <>{/* Stats rápidas */}
      {!cargando && (
        <div style={p.statsRow}>
          <StatCard icon="🍦" label="Sabores activos" valor={sabores.length} color="#3B82F6" />
          <StatCard icon="🟢" label="En vitrina" valor={totalBandejasVitrina} color="#10B981" />
          <StatCard
            icon="🔴"
            label="Agotados"
            valor={saboresAgotados.length}
            color={saboresAgotados.length > 0 ? '#EF4444' : '#9CA3AF'}
          />
          <StatCard icon="⭐" label="Premium" valor={saboresPremium.length} color="#F59E0B" />
        </div>
      )}

      {/* Alerta sabores agotados */}
      {saboresAgotados.length > 0 && (
        <div style={p.alertaAgotados}>
          <span>⚠️</span>
          <span>
            <strong>Sin stock:</strong> {saboresAgotados.map(s => s.nombre).join(', ')}
          </span>
        </div>
      )}

      {/* Grid de sabores */}
      {cargando ? (
        <div style={p.loadingWrap}><div style={p.loadingIcon}>⏳</div><p>Cargando...</p></div>
      ) : (
        <>
          {/* Sabores premium primero */}
          {saboresPremium.length > 0 && (
            <div style={p.seccion}>
              <div style={p.seccionHeader}>
                <span style={p.seccionBadge}>⭐ Premium</span>
                <span style={p.seccionLinea} />
              </div>
              <div style={p.grid}>
                {saboresPremium.map(s => (
                  <TarjetaSabor
                    key={s.id}
                    sabor={s}
                    registro={getRegistro(s)}
                    onChange={(r) => handleChange(s.id, r)}
                    modoProduccion={modoProduccion}
                    disabled={soloLectura}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sabores regulares */}
          <div style={p.seccion}>
            <div style={p.seccionHeader}>
              <span style={p.seccionBadgeReg}>Sabores</span>
              <span style={p.seccionLinea} />
            </div>
            <div style={p.grid}>
              {sabores.filter(s => !s.es_premium).map(s => (
                <TarjetaSabor
                  key={s.id}
                  sabor={s}
                  registro={getRegistro(s)}
                  onChange={(r) => handleChange(s.id, r)}
                  modoProduccion={modoProduccion}
                  disabled={soloLectura}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Leyenda interactiva */}
      {!modoProduccion && (
        <div style={p.leyenda}>
          <strong>Cómo usar las bandejas:</strong>
          <div style={p.leyendaItems}>
            {['Vacía', '⅓ llena', '⅔ llena', 'Llena'].map((l, i) => (
              <span key={i} style={p.leyendaItem}>
                <span style={{ ...p.leyendaDot, opacity: 0.3 + i * 0.23 }}>●</span> {l}
              </span>
            ))}
            <span style={p.leyendaItem}>🧊 = bandejas cerradas en congelador</span>
          </div>
        </div>
      )}

      </>
      )}

      {/* Vista Kardex */}
      {vistaTab === 'kardex' && (
        <div>
          <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Desde</div>
              <input type="date" value={desdeKardex} onChange={e => setDesdeKardex(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: 4 }}>Hasta</div>
              <input type="date" value={hastaKardex} onChange={e => setHastaKardex(e.target.value)} style={{ padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none' }} />
            </div>
            <button onClick={cargarKardex} disabled={cargandoKardex} style={{ padding: '9px 20px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              {cargandoKardex ? '⏳' : '🔍 Consultar'}
            </button>
            {kardex && (
              <div style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>
                Bandeja = {kardex.kg_bandeja} kg · {bodegaSeleccionada?.nombre}
              </div>
            )}
          </div>

          {kardex && kardex.resumen.length === 0 && (
            <div style={{ background: 'white', borderRadius: 14, padding: 32, textAlign: 'center', color: '#9CA3AF' }}>
              Sin datos de inventario para este período.
            </div>
          )}

          {kardex && kardex.resumen.length > 0 && (
            <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ background: '#1a1a2e', padding: '11px 18px' }}>
                <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>📊 Kardex de helados — {bodegaSeleccionada?.nombre}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#F9FAFB' }}>
                    {['Sabor', 'Total entradas (kg)', 'Consumo implícito (kg)', 'Stock actual (kg)'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Sabor' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kardex.resumen.map((s, i) => (
                    <tr key={s.sabor_id} style={{ borderTop: '1px solid #F3F4F6', background: i%2===0?'white':'#FAFAFA' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{s.sabor}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 700 }}>
                        {s.total_entradas > 0 ? `${s.total_entradas} kg` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', color: '#EF4444', fontWeight: 700 }}>
                        {s.total_consumo > 0 ? `${s.total_consumo} kg` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#2563EB' }}>
                        {s.stock_actual_kg > 0 ? `${s.stock_actual_kg} kg` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F3F4F6' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 800 }}>Total</td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#059669' }}>
                      {kardex.resumen.reduce((s,r) => s+r.total_entradas, 0).toFixed(1)} kg
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#EF4444' }}>
                      {kardex.resumen.reduce((s,r) => s+r.total_consumo, 0).toFixed(1)} kg
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#2563EB' }}>
                      {kardex.resumen.reduce((s,r) => s+r.stock_actual_kg, 0).toFixed(1)} kg
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal distribución */}
      {modalDist && (
        <ModalDistribucion
          sabores={sabores}
          bodegas={bodegas}
          onClose={() => setModalDist(false)}
          onSave={() => {}}
        />
      )}

      {modalConfirm && (
        <ModalConfirmacionHelados
          saboresContados={sabores.filter(s => {
            const r = getRegistro(s);
            return r.nivel_bandeja > 0 || r.bandejas_congelador > 0;
          }).length}
          totalSabores={sabores.length}
          onConfirmar={handleConfirmarYBloquear}
          onCancelar={() => setModalConfirm(false)}
        />
      )}

      {modalCorreccion && (
        <ModalSolicitudHelados
          bodegaId={bodegaSeleccionada?.id}
          fecha={fechaVer}
          onClose={() => setModalCorreccion(false)}
        />
      )}
    </div>
  );
}

function ModalConfirmacionHelados({ saboresContados, totalSabores, onConfirmar, onCancelar }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 400, padding: 32, textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🍦🔒</div>
        <h3 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px' }}>¿Confirmas el inventario del día?</h3>
        <p style={{ fontSize: 14, color: '#374151', margin: '0 0 16px', lineHeight: 1.5 }}>
          Una vez confirmado, este inventario quedará bloqueado. No podrás modificarlo sin autorización de Administración.
        </p>
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#166534' }}>
          🍦 <strong>{saboresContados}</strong> de <strong>{totalSabores}</strong> sabores con inventario registrado
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={onCancelar} style={{ padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Revisar de nuevo
          </button>
          <button onClick={onConfirmar} style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#1D4ED8,#1E40AF)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            🔒 Confirmar y Bloquear
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalSolicitudHelados({ bodegaId, fecha, onClose }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!motivo.trim()) { return; }
    setEnviando(true);
    try {
      await API.post('/correcciones/solicitar', { tipo: 'inventario_helados', registro_id: bodegaId, bodega_id: bodegaId, motivo });
      toast.success('Solicitud enviada a administración');
      onClose();
    } catch { toast.error('Error al enviar solicitud'); }
    finally { setEnviando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 400, padding: 28, boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>✏️ Solicitar Corrección</h3>
        <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>Explica qué necesitas corregir. Administración recibirá tu solicitud.</p>
        <textarea
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          placeholder="Describe el error y qué debe corregirse..."
          rows={4}
          style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16 }}
        />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Cancelar</button>
          <button onClick={handleEnviar} disabled={enviando || !motivo.trim()} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            {enviando ? 'Enviando...' : '📤 Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, valor, color }) {
  return (
    <div style={sc.card}>
      <div style={{ ...sc.icon, color }}>{icon}</div>
      <div style={{ ...sc.valor, color }}>{valor}</div>
      <div style={sc.label}>{label}</div>
    </div>
  );
}

const sc = {
  card: {
    background: 'white', borderRadius: 12, padding: '14px 20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', flex: 1, minWidth: 100,
  },
  icon: { fontSize: 22 },
  valor: { fontSize: 26, fontWeight: 800 },
  label: { fontSize: 11, color: '#9CA3AF', fontWeight: 600, textAlign: 'center' },
};

const p = {
  root: {
    fontFamily: "'Segoe UI', sans-serif",
    maxWidth: 1200, margin: '0 auto',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },
  titulo: {
    margin: 0, fontSize: 26, fontWeight: 800, color: '#111827',
    display: 'flex', alignItems: 'center', gap: 10,
  },
  tituloIcon: { fontSize: 32 },
  subtitulo: {
    margin: '4px 0 0', color: '#6B7280', fontSize: 14, textTransform: 'capitalize',
  },
  headerActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  btnDistribucion: {
    padding: '10px 18px', border: '2px solid #1a1a2e',
    borderRadius: 10, background: 'white', cursor: 'pointer',
    fontSize: 14, fontWeight: 600, color: '#1a1a2e',
    transition: 'all 0.15s',
  },
  btnGuardar: {
    padding: '10px 24px', border: 'none', borderRadius: 10,
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700,
    boxShadow: '0 4px 12px rgba(26,26,46,0.3)',
    transition: 'all 0.2s',
  },
  btnGuardarOk: {
    background: 'linear-gradient(135deg, #059669, #047857)',
    boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
  },
  bodegaRow: {
    display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap',
  },
  bodegaBtn: {
    padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 20,
    background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    color: '#6B7280', transition: 'all 0.15s',
  },
  bodegaBtnActive: {
    background: '#1a1a2e', color: 'white', borderColor: '#1a1a2e',
  },
  statsRow: {
    display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
  },
  alertaAgotados: {
    display: 'flex', gap: 10, alignItems: 'center',
    background: '#FEF2F2', border: '1px solid #FECACA',
    borderRadius: 10, padding: '12px 16px', marginBottom: 20,
    fontSize: 14, color: '#DC2626',
  },
  seccion: { marginBottom: 28 },
  seccionHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  seccionBadge: {
    background: '#FEF3C7', color: '#92400E', padding: '4px 12px',
    borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
    border: '1px solid #FCD34D',
  },
  seccionBadgeReg: {
    background: '#EFF6FF', color: '#1D4ED8', padding: '4px 12px',
    borderRadius: 20, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
    border: '1px solid #BFDBFE',
  },
  seccionLinea: { flex: 1, height: 1, background: '#E5E7EB' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 14,
  },
  leyenda: {
    marginTop: 8, padding: '14px 18px',
    background: '#F9FAFB', borderRadius: 10, fontSize: 13,
    border: '1px solid #E5E7EB', color: '#6B7280',
  },
  leyendaItems: {
    display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 6,
  },
  leyendaItem: { display: 'flex', alignItems: 'center', gap: 5 },
  leyendaDot: { fontSize: 16, color: '#6366F1' },
  loadingWrap: { textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' },
  loadingIcon: { fontSize: 48, marginBottom: 12 },
};
